import {Component, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {
    CampaignProfilePrlObj,
    IdNameMxObj,
    MxButtonModel,
    MxInformationTooltipConfig,
    MxPreloaderService,
    withPreloader
} from 'mx-core';
import {combineLatestAll, of, Subject} from 'rxjs';
import {PrlMarketingToolsService, SearchData} from '../../services/prl-marketing-tools.service';
import {takeUntil} from 'rxjs/operators';
import {MarketingToolsDataPrlObj, ZoneLightPrlObj} from '../../../../../../generated/generated.model';
import {MatSelectChange} from '@angular/material/select';
import {PrlProfilesService} from '../../../../shared/services/prl-profiles.service';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {PrlTranslateService} from "../../../../shared/services/prl-translate.service";

type RelationData = {
    [key: string]: IdNameMxObj[];
};

@Component({
    selector: 'prl-marketing-tools',
    templateUrl: './prl-marketing-tools.component.html',
    standalone: false,
    styleUrls: ['./prl-marketing-tools.component.scss']
})
export class PrlMarketingToolsComponent implements OnInit {
    infoConfig: MxInformationTooltipConfig = {
        links: [],
        header: this.translateService.getTranslatedLabel('label.online.help'),
        content: this.translateService.getTranslatedLabel('label.marketing.tools.sort.by.help'),
    };

    protected isMobile: boolean = false;

    protected allCurrencies: Array<IdNameMxObj> = [];
    protected availableCurrencies: Array<IdNameMxObj> = [];
    protected allLanguages: Array<IdNameMxObj> = [];
    protected availableLanguages: Array<IdNameMxObj> = [];
    protected allProducts: Array<IdNameMxObj> = [];
    protected availableProducts: Array<IdNameMxObj> = [];
    protected allProductTypes: Array<IdNameMxObj> = [];
    protected availableProductSubTypes: Array<IdNameMxObj> = [];
    protected allProductSubTypes: Array<IdNameMxObj> = [];

    protected currencyControl: FormControl = new FormControl('');
    protected descriptionControl: FormControl = new FormControl('');
    protected creativeDescriptionControl: FormControl = new FormControl('');
    protected languageControl: FormControl = new FormControl('');
    protected productControl: FormControl = new FormControl('');
    protected controlsMap = new Map<string, FormControl>();
    protected sortControl: FormControl = new FormControl('');

    protected searchButton: MxButtonModel;
    protected resetButton: MxButtonModel;
    protected listButton: MxButtonModel;
    protected galleryButton: MxButtonModel;

    protected filteredZones: ZoneLightPrlObj[];
    protected selectedSortOption: IdNameMxObj;
    protected selectedView: 'list' | 'gallery' = 'list';

    protected sortOptions: IdNameMxObj[] = new Array<IdNameMxObj>();
    private currencyControlSelection: IdNameMxObj[] = new Array<IdNameMxObj>();
    private languageControlSelection: IdNameMxObj[] = new Array<IdNameMxObj>();
    private productControlSelection: IdNameMxObj[] = new Array<IdNameMxObj>();
    private productSubTypeControlSelection: IdNameMxObj[] = new Array<IdNameMxObj>();

    private isSearchDisabled: boolean = false;
    private searchData: SearchData;
    private destroy$$: Subject<null> = new Subject<null>();
    private fullData: MarketingToolsDataPrlObj;

    constructor(
        private readonly marketingToolsService: PrlMarketingToolsService,
        private readonly profilesService: PrlProfilesService,
        private readonly preloader: MxPreloaderService,
        private readonly breakpointObserver: BreakpointObserver,
        private readonly translateService: PrlTranslateService
    ) {
    }

    ngOnInit(): void {
        this.breakpointObserver
            .observe([Breakpoints.Handset])
            .pipe(takeUntil(this.destroy$$))
            .subscribe(result => {
                this.isMobile = result.matches;
                if (this.isMobile) {
                    // On phones only the gallery view is available
                    this.selectedView = 'gallery';
                }
            });

        this.searchButton = {
            label: 'label.marketing.tools.search',
            tooltip: 'label.marketing.tools.search',
            className: 'mxs-button--primary',
            disabled: this.isSearchDisabled,
            clickHandler: this.performSearch
        };
        this.resetButton = {
            label: 'label.reset',
            tooltip: 'label.reset',
            className: 'mxs-button--primary',
            disabled: this.isSearchDisabled,
            clickHandler: this.resetFilters
        };
        this.listButton = {
            icon: 'view_headline',
            tooltip: 'label.marketing.tools.view.list.tooltip',
            disabled: () => this.selectedView === 'list',
            clickHandler: () => this.selectedView = 'list'
        };
        this.galleryButton = {
            icon: 'grid_view',
            tooltip: 'label.marketing.tools.view.gallery.tooltip',
            disabled: () => this.selectedView === 'gallery',
            clickHandler: () => this.selectedView = 'gallery'
        };

        this.sortOptions.push(
            {id: 'Newest', name: 'label.marketing.tools.newest'},
            {id: 'TopPerformance', name: 'label.marketing.tools.top.performance'},
            {id: 'Creative', name: 'label.marketing.tools.zone.creative'},
            {id: 'CreativeType', name: 'label.marketing.tools.zone.type'},
            {id: 'Brand', name: 'label.marketing.tools.zone.brand'},
            {id: 'Language', name: 'label.marketing.tools.zone.languages'},
            {id: 'Currency', name: 'label.marketing.tools.zone.currencies'});

        this.selectedSortOption = {id: 'Newest', name: 'label.marketing.tools.newest'};

        this.sortControl.setValue(this.sortOptions[0]);
        this.searchData = {
            subTypeIds: [],
            productIds: [],
            languages: [],
            currencies: [],
            description: null,
            creativeDescription: null,
            order: [],
            orderBy: this.sortOptions[0].id
        };
        of(
            this.marketingToolsService.getFiltersData(),
            this.marketingToolsService.getLightZones(this.searchData),
            this.marketingToolsService.loadProfiles(),
            this.profilesService.loadNonExpiredCampaigns()
        ).pipe(takeUntil(this.destroy$$), withPreloader(this.preloader), combineLatestAll())
            .subscribe((response) => {
                this.parseFilterData(response[0] as MarketingToolsDataPrlObj);
                this.filteredZones = response[1] as ZoneLightPrlObj[];
                this.marketingToolsService.setProfiles(response[2] as Map<number, CampaignProfilePrlObj[]>);
                this.profilesService.setNonExpiredCampaigns(response[3] as IdNameMxObj[]);
            });
    }

    protected selectSortOption($event: MatSelectChange<any>) {
        this.selectedSortOption = $event.value as IdNameMxObj;
    }

    protected getAvailableSubtype(id: string): IdNameMxObj[] {
        const allByType = this.fullData.typeToSubTypes[id];
        const ids = new Set(allByType.map(item => item.id));
        return this.availableProductSubTypes.filter(item => ids.has(item.id));
    }

    protected filterChanged(event: MatSelectChange<any>) {
        const currentControl = event.source.ngControl.control as FormControl;
        const selection = event.value as IdNameMxObj[];

        switch (currentControl) {
            case this.productControl: {
                this.availableLanguages = this.getUniqArray(this.fullData.productToLanguages, selection);
                this.availableCurrencies = this.getUniqArray(this.fullData.productToCurrencies, selection);
                this.availableProductSubTypes = this.getUniqArray(this.fullData.productToSubTypes, selection);
                this.productControlSelection = selection;
                this.languageControl.setValue(
                    this.getSelection(this.availableLanguages, this.languageControlSelection), {emitEvent: false});
                this.currencyControl.setValue(
                    this.getSelection(this.availableCurrencies, this.currencyControlSelection), {emitEvent: false});
                for (let control of this.controlsMap.values()) {
                    control.setValue(
                        this.getSelection(this.availableProductSubTypes, this.productSubTypeControlSelection), {emitEvent: false});
                }
                break;
            }
            case this.languageControl: {
                this.availableProducts = this.getUniqArray(this.fullData.languageToProducts, selection);
                this.availableCurrencies = this.getUniqArray(this.fullData.languageToCurrencies, selection);
                this.availableProductSubTypes = this.getUniqArray(this.fullData.languageToSubTypes, selection);
                this.languageControlSelection = selection;
                this.productControl.setValue(
                    this.getSelection(this.availableProducts, this.productControlSelection), {emitEvent: false});
                this.currencyControl.setValue(
                    this.getSelection(this.availableCurrencies, this.currencyControlSelection), {emitEvent: false});
                for (let control of this.controlsMap.values()) {
                    control.setValue(
                        this.getSelection(this.availableProductSubTypes, this.productSubTypeControlSelection), {emitEvent: false});
                }
                break;
            }
            case this.currencyControl: {
                this.availableProducts = this.getUniqArray(this.fullData.currencyToProducts, selection);
                this.availableLanguages = this.getUniqArray(this.fullData.currencyToLanguages, selection);
                this.availableProductSubTypes = this.getUniqArray(this.fullData.currencyToSubTypes, selection);
                this.currencyControlSelection = selection;
                this.productControl.setValue(
                    this.getSelection(this.availableProducts, this.productControlSelection), {emitEvent: false});
                this.languageControl.setValue(
                    this.getSelection(this.availableLanguages, this.languageControlSelection), {emitEvent: false});
                for (let control of this.controlsMap.values()) {
                    control.setValue(this.getSelection(
                        this.availableProductSubTypes, this.productSubTypeControlSelection), {emitEvent: false});
                }
                break;
            }
            default: {
                this.availableProducts = this.getUniqArray(this.fullData.subTypeToProducts, selection);
                this.availableCurrencies = this.getUniqArray(this.fullData.subTypeToCurrencies, selection);
                this.availableLanguages = this.getUniqArray(this.fullData.subTypeToLanguages, selection);
                this.productSubTypeControlSelection = selection;
                this.productControl.setValue(
                    this.getSelection(this.availableProducts, this.productControlSelection), {emitEvent: false});
                this.currencyControl.setValue(
                    this.getSelection(this.availableCurrencies, this.currencyControlSelection), {emitEvent: false});
                this.languageControl.setValue(
                    this.getSelection(this.availableLanguages, this.languageControlSelection), {emitEvent: false});
                break;
            }
        }
    }

    protected compareById = (a: IdNameMxObj | null, b: IdNameMxObj | null): boolean => {
        return a?.id === b?.id;
    };

    private performSearch = (): any => {
        this.searchData.currencies = this.toKeyArray(this.currencyControlSelection);
        this.searchData.languages = this.toKeyArray(this.languageControlSelection);
        this.searchData.productIds = this.toKeyArray(this.productControlSelection);
        this.searchData.subTypeIds = this.toKeyArray(this.productSubTypeControlSelection);
        this.searchData.orderBy = this.selectedSortOption.id;
        this.searchData.description = this.descriptionControl.value?.trim() || null;
        this.searchData.creativeDescription = this.creativeDescriptionControl.value?.trim() || null;
        of(
            this.marketingToolsService.getLightZones(this.searchData)
        ).pipe(takeUntil(this.destroy$$), withPreloader(this.preloader), combineLatestAll())
            .subscribe((response) => {
                this.filteredZones = response[0];
            });
    };

    private toKeyArray = (array: IdNameMxObj[]): string[] => {
        return array.map(item => item.id);
    };

    private resetFilters = () => {
        this.availableProducts = this.allProducts;
        this.availableLanguages = this.allLanguages;
        this.availableCurrencies = this.allCurrencies;
        this.availableProductSubTypes = this.allProductSubTypes;

        this.productControl.setValue(null);
        this.languageControl.setValue(null);
        this.currencyControl.setValue(null);
        this.descriptionControl.setValue('');
        this.creativeDescriptionControl.setValue('');
        for (let control of this.controlsMap.values()) {
            control.setValue(null);
        }

        this.productControlSelection = [];
        this.languageControlSelection = [];
        this.currencyControlSelection = [];
        this.productSubTypeControlSelection = [];
    };

    private parseFilterData = (filterData: MarketingToolsDataPrlObj) => {
        this.fullData = filterData;
        this.allCurrencies = this.getUniqArray(filterData.subTypeToCurrencies);
        this.availableCurrencies.push(...this.allCurrencies);
        this.allLanguages = this.getUniqArray(filterData.subTypeToLanguages);
        this.availableLanguages.push(...this.allLanguages);
        this.allProducts = this.getUniqArray(filterData.subTypeToProducts);
        this.availableProducts.push(...this.allProducts);
        this.allProductTypes = this.getUniqArray(filterData.subTypeToTypes);
        this.availableProductSubTypes = this.getUniqArray(filterData.productToSubTypes);
        this.allProductSubTypes.push(...this.availableProductSubTypes);

        this.allProductTypes.forEach(item => {
            this.controlsMap.set(item.id, new FormControl(''));
        });
    };

    private getUniqArray(
        arrays: RelationData,
        allowedIds?: IdNameMxObj[]
    ): IdNameMxObj[] {

        const allowedSet = allowedIds
            ? new Set(allowedIds.map(i => i.id))
            : null;

        return Object.entries(arrays)
            .filter(([key]) => !allowedSet || (allowedSet.size === 0) || allowedSet.has(key))
            .flatMap(([_, values]) => values)
            .filter(
                (item, index, arr) =>
                    arr.findIndex(el => el.id === item.id) === index
            );
    }

    private getSelection = (allValues: Array<IdNameMxObj>,
                            selection: IdNameMxObj[]): IdNameMxObj[] => {
        const idsSet = new Set(selection.map(i => i.id));
        return allValues.filter(item => idsSet.has(item.id));
    };
}
